/**
 * useBatchIdSearch Hook
 * Provides debounced search functionality for inspection lots by batch ID
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { BRRApiClient } from '../services/api';

interface UseBatchIdSearchOptions {
  apiClient: BRRApiClient;
  batchId: string;
  debounceMs?: number;
  minQueryLength?: number;
}

interface InspectionLotResult {
  lotId: string;
}

interface UseBatchIdSearchReturn {
  results: InspectionLotResult[];
  loading: boolean;
  error: Error | null;
}

/**
 * Hook that searches inspection lots by batch ID with debouncing
 *
 * @example
 * const { results, loading, error } = useBatchIdSearch({
 *   apiClient: brrApi,
 *   batchId: searchText,
 *   debounceMs: 500,
 *   minQueryLength: 3,
 * });
 */
export const useBatchIdSearch = ({
  apiClient,
  batchId,
  debounceMs = 500,
  minQueryLength = 3,
}: UseBatchIdSearchOptions): UseBatchIdSearchReturn => {
  const [results, setResults] = useState<InspectionLotResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getInspectionLotsByBatch(searchQuery);
      setResults(response.results);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, minQueryLength]);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Return early if query is too short
    if (batchId.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    // Set new timeout
    setLoading(true);
    timeoutRef.current = window.setTimeout(() => {
      void search(batchId);
    }, debounceMs);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [batchId, debounceMs, minQueryLength, search]);

  return {
    results,
    loading,
    error
  };
};
